import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, Button, Typography, Icon } from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { useDialog } from '../../../../common/hooks';
import { DataTable, EmptyState } from '../../../../common/components';
import { Stream } from '../../../../types';
import { DateUtils } from '../../../../common/utils';

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
}));

export type StreamListProps = {
  data: Array<Stream>;
  onDeleteStream: (streamId: string) => void;
  actionsDisabled: boolean;
};

function StreamList(props: StreamListProps) {
  const [selectedStream, setSelectedStream] = useState<string>();
  const [t] = useTranslation();
  // @ts-expect-error
  const { showDialog } = useDialog();
  const classes = useStyles();
  const { data, onDeleteStream, actionsDisabled } = props;

  const getColumnDefs = () => [
    {
      field: 'userId',
      headerName: t('stream.userId'),
      width: 140,
    },
    {
      field: 'streamLockToken',
      headerName: t('stream.streamLockToken'),
      width: 290,
    },
    {
      field: 'ipAddress',
      headerName: t('stream.ipAddress'),
      width: 150,
    },
    {
      field: 'playContentId',
      headerName: t('stream.playContentId'),
      width: 290,
    },
    {
      field: 'platform',
      headerName: t('stream.platform'),
      width: 130,
    },
    {
      field: 'platformClass',
      headerName: t('stream.platformClass'),
      width: 200,
    },
    {
      field: 'platformAppVersion',
      headerName: t('stream.platformAppVersion'),
      width: 200,
    },
    {
      field: 'platformAudience',
      headerName: t('stream.platformAudience'),
      width: 200,
    },
    {
      field: 'lastUpdatedAt',
      headerName: t('stream.lastUpdatedAt'),
      width: 200,
    },
    {
      field: 'expirationTime',
      headerName: t('stream.expirationTime'),
      width: 200,
    },
  ];

  const getRowData = () =>
    data.map(stream => ({
      userId: stream.userId,
      streamLockToken: stream.streamLockToken,
      ipAddress: stream.ipAddress,
      playContentId: stream.playContentId,
      platform: stream.platform,
      platformClass: stream.platformClass,
      platformAppVersion: stream.platformAppVersion,
      platformAudience: stream.platformAudience,
      lastUpdatedAt: DateUtils.getDateTime(stream.lastUpdatedAt),
      expirationTime: DateUtils.getDateTime(stream.expirationTime * 1000),
      id: stream.streamLockToken,
    }));

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('stream.empty')} />
    </GridOverlay>
  );

  const handleSelectStreams = (params: { selectionModel: Array<string> }) => {
    const { selectionModel } = params;
    setSelectedStream(selectionModel.at(0));
  };

  const handleShowDeleteStreamsDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('stream.streamsDeleteConfirm')}</Typography>,
      onConfirm: () => {
        if (selectedStream) onDeleteStream(selectedStream);
      },
    });
  };

  const renderToolbar = () => (
    <GridToolbarContainer>
      <Button
        id="StreamList-deleteStreams"
        variant="outlined"
        color="primary"
        startIcon={<Icon>delete</Icon>}
        onClick={handleShowDeleteStreamsDialog}
        disabled={actionsDisabled || _.isEmpty(selectedStream)}
      >
        {t('stream.streamsDelete')}
      </Button>
    </GridToolbarContainer>
  );

  return (
    <Box id="StreamList-container" className={classes.container}>
      <DataTable
        id="StreamList-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        onSelectionModelChange={handleSelectStreams}
        pageSize={20}
        paginationMode="client"
        pagination
      />
    </Box>
  );
}

export default StreamList;
